<template>
  <div class="settings-subscription">
    <div class="settings-card-top"></div>

    <div class="settings-card-body">
      <br />
      <h4>Suscripción</h4>
      <p class="settings-subtitle">Administra tu paquete, vigencia, próximo pago, métodos de pago e historial de pagos y facturas.</p>
      <div v-if="sectionError" class="alert alert-danger" role="alert">{{ sectionError }}</div>

      <div class="subscription-summary-grid">
        <article class="subscription-summary-card">
          <span class="summary-label">Paquete contratado</span>
          <h5>{{ packageName }}</h5>
          <small>{{ packageRfcText }}</small>
        </article>

        <article class="subscription-summary-card">
          <span class="summary-label">Vigencia actual</span>
          <h5>{{ validityRange }}</h5>
          <small>{{ subscriptionStatusText }}</small>
        </article>

        <article class="subscription-summary-card">
          <span class="summary-label">Próximo pago</span>
          <h5>{{ nextPaymentDate }}</h5>
          <small>{{ nextPaymentAmount }}</small>
        </article>
      </div>

      <div class="subscription-panels-grid">
        <section class="subscription-panel payment-method-panel">
          <div class="panel-head">
            <h6>Método de pago</h6>
            <button
              v-if="primaryCard"
              type="button"
              class="btn btn-sm btn-outline-danger btn-icon-only"
              :disabled="deletingCard"
              title="Eliminar tarjeta"
              aria-label="Eliminar tarjeta"
              @click="openDeleteCardModal()"
            >
              <i class="fa" :class="deletingCard ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
            </button>
            <span v-else class="panel-badge">Conekta</span>
          </div>

          <div v-if="primaryCard" class="card-registered-box">
            <div class="card-brand">{{ primaryCard.brand || 'Tarjeta' }}</div>
            <div class="card-number">•••• •••• •••• {{ primaryCard.last4 || '0000' }}</div>
            <div class="card-meta">
              <span>Expira {{ cardExpiration(primaryCard) }}</span>
            </div>

            <div class="card-detail-grid">
              <div>
                <span class="card-detail-label">Titular</span>
                <p>{{ cardHolder(primaryCard) }}</p>
              </div>
            </div>
          </div>

          <div v-else class="card-empty-box">
            <i class="fa fa-credit-card"></i>
            <p>No has registrado tu información de pago. Agrega una tarjeta para activar el pago automático de tu suscripción.</p>
          </div>

          <div class="subscription-actions-inline">
            <button v-if="!primaryCard" type="button" class="btn btn-primary" @click="openCardModal()">
              Registrar tarjeta
            </button>
          </div>
        </section>

        <section class="subscription-panel purchase-panel">
          <div class="panel-head">
            <h6>Renovación o cambio de paquete</h6>
          </div>

          <div class="settings-field">
            <label>Selecciona paquete</label>
            <select class="form-control" v-model="selectedPackageId">
              <option v-for="item in packageCatalog" :key="item.id" :value="item.id">
                {{ packageOptionLabel(item) }}
              </option>
            </select>
          </div>

          <div v-if="purchaseFormError" class="alert alert-danger mt-2 mb-2" role="alert">{{ purchaseFormError }}</div>

          <p class="purchase-note"><br />Al confirmar, se realizará el cargo con tu tarjeta activa y se configurará la suscripción recurrente del paquete seleccionado.</p>

          <div class="subscription-actions-inline purchase-actions-inline">
            <button type="button" class="btn btn-primary" :disabled="quotingPurchase || purchasingSubscription" @click="openPurchaseModal()">
              {{ quotingPurchase ? 'Calculando...' : 'Comprar paquete' }}
            </button>
          </div>
        </section>
      </div>

      <section class="subscription-panel history-panel">
        <div class="panel-head">
          <h6>Historial de pagos</h6>
        </div>

        <div class="table-responsive" v-if="paymentHistory.length > 0">
          <table class="table table-sm subscription-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Concepto</th>
                <th>Monto</th>
                <th>Estatus</th>
                <th class="invoice-col">Factura</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in paymentHistory" :key="item.id">
                <td>{{ formatDate(item.date) }}</td>
                <td>{{ item.description }}</td>
                <td>{{ formatCurrency(item.amount) }}</td>
                <td>
                  <span class="badge" :class="item.status === 'paid' ? 'badge-light-success' : 'badge-light-warning'">
                    {{ item.status === 'paid' ? 'Pagado' : 'Pendiente' }}
                  </span>
                </td>
                <td class="invoice-col">
                  <div class="invoice-actions">
                    <button
                      v-if="shouldShowInvoiceDownloads(item)"
                      type="button"
                      class="btn btn-sm btn-light"
                      :disabled="!canDownloadInvoiceXml(item)"
                      @click="downloadInvoiceXml(item)"
                    >
                      Ver XML
                    </button>
                    <button
                      v-if="shouldShowInvoiceDownloads(item)"
                      type="button"
                      class="btn btn-sm btn-light"
                      :disabled="!canDownloadInvoicePdf(item)"
                      @click="downloadInvoicePdf(item)"
                    >
                      Ver PDF
                    </button>
                    <button
                      v-if="canIssueInvoice(item)"
                      type="button"
                      class="btn btn-sm btn-primary"
                      :disabled="issuingInvoiceChargeId === item.chargeId"
                      @click="issueInvoice(item)"
                    >
                      {{ issuingInvoiceChargeId === item.chargeId ? 'Facturando...' : 'Facturar' }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else class="history-empty">Aun no hay pagos/cargos registrados.</p>
      </section>
    </div>

    <div v-if="showCardModal" class="payment-modal-backdrop" @click.self="closeCardModal">
      <div class="payment-modal" role="dialog" aria-modal="true" aria-labelledby="payment-card-modal-title">
        <div class="payment-modal-header">
          <h5 id="payment-card-modal-title">Registrar tarjeta</h5>
          <button type="button" class="btn-close" aria-label="Cerrar" @click="closeCardModal"></button>
        </div>

        <form ref="cardForm" class="payment-modal-body" autocomplete="off" @submit.prevent="submitCardModal">
          <div v-if="cardFormError" class="alert alert-danger" role="alert">{{ cardFormError }}</div>

          <input type="hidden" :value="cardModalForm.expMonth" data-conekta="card[exp_month]" />
          <input type="hidden" :value="cardModalForm.expYear" data-conekta="card[exp_year]" />
          <input type="hidden" :value="cardModalForm.country" data-conekta="card[address][country]" />

          <div class="settings-grid payment-modal-grid">
            <div class="settings-field">
              <label>Nombre del titular</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.holder }"
                type="text"
                v-model.trim="cardModalForm.holder"
                @input="onUppercaseInput('holder', $event)"
                @blur="touchCardField('holder')"
                placeholder="Nombre completo"
                name="card-holder-name"
                autocomplete="cc-name"
                data-conekta="card[name]"
              />
              <div v-if="cardFieldErrors.holder" class="invalid-feedback d-block">{{ cardFieldErrors.holder }}</div>
            </div>

            <div class="settings-field">
              <label>Número de tarjeta</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.number }"
                type="text"
                v-model.trim="cardModalForm.number"
                @input="onCardNumberInput"
                @blur="touchCardField('number')"
                placeholder="4242 4242 4242 4242"
                inputmode="numeric"
                name="card-number"
                autocomplete="cc-number"
                maxlength="16"
                data-conekta="card[number]"
              />
              <div v-if="cardFieldErrors.number" class="invalid-feedback d-block">{{ cardFieldErrors.number }}</div>
            </div>

            <div class="settings-field">
              <label>Expiración</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.expiration }"
                type="text"
                v-model.trim="cardModalForm.expiration"
                @input="onExpirationInput"
                @blur="touchCardField('expiration')"
                placeholder="MM/AA"
                maxlength="5"
                name="card-expiration"
                autocomplete="cc-exp"
                data-lpignore="true"
              />
              <div v-if="cardFieldErrors.expiration" class="invalid-feedback d-block">{{ cardFieldErrors.expiration }}</div>
            </div>

            <div class="settings-field">
              <label>CVV</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.cvv }"
                type="password"
                v-model.trim="cardModalForm.cvv"
                @input="onCvvInput"
                @blur="touchCardField('cvv')"
                placeholder="***"
                inputmode="numeric"
                name="card-cvc"
                autocomplete="cc-csc"
                data-lpignore="true"
                maxlength="4"
                data-conekta="card[cvc]"
              />
              <div v-if="cardFieldErrors.cvv" class="invalid-feedback d-block">{{ cardFieldErrors.cvv }}</div>
            </div>

            <div class="settings-field payment-modal-span-2">
              <label>Dirección</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.street }"
                type="text"
                v-model.trim="cardModalForm.street"
                @input="onUppercaseInput('street', $event)"
                @blur="touchCardField('street')"
                placeholder="Calle y número"
                data-conekta="card[address][street1]"
              />
              <div v-if="cardFieldErrors.street" class="invalid-feedback d-block">{{ cardFieldErrors.street }}</div>
            </div>

            <div class="settings-field">
              <label>Colonia</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.colony }"
                type="text"
                v-model.trim="cardModalForm.colony"
                @input="onUppercaseInput('colony', $event)"
                @blur="touchCardField('colony')"
                placeholder="Colonia"
                data-conekta="card[address][street2]"
              />
              <div v-if="cardFieldErrors.colony" class="invalid-feedback d-block">{{ cardFieldErrors.colony }}</div>
            </div>

            <div class="settings-field">
              <label>Ciudad</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.city }"
                type="text"
                v-model.trim="cardModalForm.city"
                @input="onUppercaseInput('city', $event)"
                @blur="touchCardField('city')"
                placeholder="Ciudad"
                data-conekta="card[address][city]"
              />
              <div v-if="cardFieldErrors.city" class="invalid-feedback d-block">{{ cardFieldErrors.city }}</div>
            </div>

            <div class="settings-field">
              <label>Estado</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.state }"
                type="text"
                v-model.trim="cardModalForm.state"
                @input="onUppercaseInput('state', $event)"
                @blur="touchCardField('state')"
                placeholder="Estado"
                data-conekta="card[address][state]"
              />
              <div v-if="cardFieldErrors.state" class="invalid-feedback d-block">{{ cardFieldErrors.state }}</div>
            </div>

            <div class="settings-field">
              <label>Código postal</label>
              <input
                class="form-control"
                :class="{ 'is-invalid': !!cardFieldErrors.zipcode }"
                type="text"
                v-model.trim="cardModalForm.zipcode"
                @input="onZipcodeInput"
                @blur="touchCardField('zipcode')"
                placeholder="00000"
                inputmode="numeric"
                maxlength="5"
                data-conekta="card[address][zip]"
              />
              <div v-if="cardFieldErrors.zipcode" class="invalid-feedback d-block">{{ cardFieldErrors.zipcode }}</div>
            </div>
          </div>
        </form>

        <div class="payment-modal-footer">
          <button type="button" class="btn btn-light" @click="closeCardModal">Cancelar</button>
          <button type="button" class="btn btn-primary" :disabled="savingCard" @click="submitCardModal">
            {{ savingCard ? 'Guardando...' : 'Guardar tarjeta' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDeleteCardModal" class="payment-modal-backdrop" @click.self="closeDeleteCardModal">
      <div class="payment-modal payment-modal--sm" role="dialog" aria-modal="true" aria-labelledby="delete-card-modal-title">
        <div class="payment-modal-header">
          <h5 id="delete-card-modal-title">Eliminar tarjeta</h5>
          <button type="button" class="btn-close" aria-label="Cerrar" @click="closeDeleteCardModal"></button>
        </div>

        <div class="payment-modal-body">
          <div v-if="deleteCardError" class="alert alert-danger" role="alert">{{ deleteCardError }}</div>
          <p class="payment-modal-text">Se eliminará la tarjeta registrada para pago automático.</p>
          <p class="payment-modal-text"><strong>Esta acción no se puede deshacer.</strong></p>
        </div>

        <div class="payment-modal-footer">
          <button type="button" class="btn btn-light" :disabled="deletingCard" @click="closeDeleteCardModal">Cancelar</button>
          <button type="button" class="btn btn-danger" :disabled="deletingCard" @click="deleteCard()">
            {{ deletingCard ? 'Eliminando...' : 'Sí, eliminar' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showPurchaseModal" class="payment-modal-backdrop" @click.self="closePurchaseModal">
      <div class="payment-modal payment-modal--sm payment-modal--purchase" role="dialog" aria-modal="true" aria-labelledby="purchase-modal-title">
        <div class="payment-modal-header">
          <h5 id="purchase-modal-title">Confirmar Compra</h5>
          <button type="button" class="btn-close" aria-label="Cerrar" :disabled="purchasingSubscription" @click="closePurchaseModal"></button>
        </div>

        <div class="payment-modal-body" v-if="purchaseQuote">
          <div v-if="purchaseModalError" class="alert alert-danger" role="alert">{{ purchaseModalError }}</div>

          <div class="purchase-modal-hero">
            <div class="purchase-modal-hero-icon" aria-hidden="true">
              <i class="fa fa-shopping-bag"></i>
            </div>
            <div>
              <h6>Estás por adquirir un nuevo paquete</h6>
              <p>El cargo se realizará a la tarjeta registrada.</p>
            </div>
          </div>

          <div class="purchase-modal-metrics">
            <article class="purchase-modal-metric">
              <span>Paquete</span>
              <strong>{{ purchaseQuote.package?.name || '-' }}</strong>
            </article>
            <article class="purchase-modal-metric">
              <span>Monto de este cargo</span>
              <strong>{{ formatCurrency(purchaseQuote.amount?.value || 0) }}</strong>
            </article>
            <article class="purchase-modal-metric">
              <span>Periodicidad</span>
              <strong>{{ purchaseQuote.billing_cycle === 'monthly' ? 'Mensual' : 'Anual' }}</strong>
            </article>
            <article class="purchase-modal-metric">
              <span>Nueva Vigencia</span>
              <strong>{{ formatDate(purchaseQuote.starts_at) }} al {{ formatDate(purchaseQuote.ends_at) }}</strong>
            </article>
          </div>

          <p v-if="purchaseQuote.operation_type === 'upgrade_proration'" class="payment-modal-text">
            Se aplicará un ajuste proporcional por los días remanentes de tu plan actual.
          </p>

          <div class="purchase-modal-next-charge">
            <span>Próximo pago</span>
            <strong>{{ formatDate(purchaseQuote.next_charge_at || purchaseQuote.ends_at) }}</strong>
            <small>por {{ formatCurrency(purchaseQuote.amount_breakdown?.selected_package_amount || purchaseQuote.amount?.value || 0) }}</small>
          </div>
        </div>

        <div class="payment-modal-footer">
          <button type="button" class="btn btn-light" :disabled="purchasingSubscription" @click="closePurchaseModal">Cancelar</button>
          <button type="button" class="btn btn-primary" :disabled="purchasingSubscription" @click="confirmPurchase()">
            {{ purchasingSubscription ? 'Procesando...' : (purchaseQuote?.operation_type === 'upgrade_proration' ? 'Confirmar ajuste y pagar' : 'Confirmar y pagar') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showPurchaseResultModal" class="payment-modal-backdrop" @click.self="closePurchaseResultModal">
      <div class="payment-modal payment-modal--sm payment-modal--result" role="dialog" aria-modal="true" aria-labelledby="purchase-result-modal-title">
        <div class="payment-modal-header">
          <h5 id="purchase-result-modal-title">Compra realizada</h5>
          <button type="button" class="btn-close" aria-label="Cerrar" @click="closePurchaseResultModal"></button>
        </div>

        <div class="payment-modal-body" v-if="purchaseResult">
          <div class="purchase-modal-hero purchase-modal-hero--success">
            <div class="purchase-modal-hero-icon" aria-hidden="true">
              <i class="fa fa-check"></i>
            </div>
            <div>
              <h6>Pago aplicado correctamente</h6>
              <p>Tu suscripción quedó actualizada y activa.</p>
            </div>
          </div>

          <div class="purchase-modal-metrics">
            <article class="purchase-modal-metric">
              <span>Paquete contratado</span>
              <strong>{{ purchaseResult.package?.name || '-' }}</strong>
            </article>
            <article class="purchase-modal-metric">
              <span>Monto pagado</span>
              <strong>{{ formatCurrency(purchaseResult.amount?.value || purchaseQuote?.amount?.value || 0) }}</strong>
            </article>
            <article class="purchase-modal-metric purchase-modal-metric--full">
              <span>Vigencia aplicada</span>
              <strong>{{ formatDate(purchaseResult.period?.starts_at || purchaseQuote?.starts_at) }} al {{ formatDate(purchaseResult.period?.ends_at || purchaseResult.ends_at || purchaseQuote?.ends_at) }}</strong>
            </article>
          </div>

          <p v-if="purchaseResult.operation_type === 'upgrade_proration'" class="payment-modal-text">
            El pago aplicado corresponde al ajuste por días remanentes.
          </p>

          <div class="purchase-modal-next-charge purchase-modal-next-charge--success">
            <span>Próximo pago</span>
            <strong>{{ formatDate(purchaseResult.next_charge_at || purchaseResult.period?.ends_at || purchaseResult.ends_at) }}</strong>
            <small>por {{ formatCurrency(purchaseResult.amount_breakdown?.selected_package_amount || purchaseResult.amount?.value || 0) }}</small>
          </div>

          <p class="payment-modal-text payment-modal-text--muted"><strong>ID de orden:</strong> {{ purchaseResult.order_id || '-' }}</p>
        </div>

        <div class="payment-modal-footer">
          <button type="button" class="btn btn-primary" @click="closePurchaseResultModal">Aceptar</button>
        </div>
      </div>
    </div>

    <div v-if="showBillingWarningModal" class="payment-modal-backdrop" @click.self="closeBillingWarningModal">
      <div class="payment-modal payment-modal--sm" role="dialog" aria-modal="true" aria-labelledby="billing-warning-modal-title">
        <div class="payment-modal-header">
          <h5 id="billing-warning-modal-title">Aviso de facturación</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Cerrar"
            :disabled="purchasingSubscription"
            @click="closeBillingWarningModal"
          ></button>
        </div>

        <div class="payment-modal-body">
          <p class="payment-modal-text mb-2">
            Antes de procesar cualquier pago en Tus CFDIs es necesario registrar la información fiscal para facturar la compra.
          </p>
          <p class="payment-modal-text mb-0">
            Si no registras tus datos fiscales, la factura se emitirá a público en general.
          </p>

          <div v-if="billingWarningFormError" class="alert alert-danger mt-3 mb-2" role="alert">{{ billingWarningFormError }}</div>

          <div class="billing-cif-upload-box mt-3">
            <div class="billing-cif-upload-head">
              <div>
                <h6>Cédula fiscal (PDF)</h6>
                <p>Opcional. Al seleccionar el PDF se leerán automáticamente algunos campos.</p>
              </div>
              <div class="billing-cif-actions">
                <label class="btn btn-light btn-sm mb-0">
                  {{ parsingBillingWarningCif ? 'Leyendo...' : 'Seleccionar PDF' }}
                  <input type="file" accept="application/pdf,.pdf" class="d-none" @change="onBillingWarningCifSelected" />
                </label>
              </div>
            </div>
            <small v-if="billingWarningCifFile">Archivo seleccionado: {{ billingWarningCifFile.name }}</small>
            <small v-if="billingWarningCifError" class="text-danger d-block mt-2">{{ billingWarningCifError }}</small>
          </div>

          <div class="settings-grid billing-grid mt-3">
            <div class="settings-field billing-span-1">
              <label>RFC *</label>
              <input type="text" class="form-control" v-model.trim="billingWarningForm.rfc" maxlength="13" @input="onBillingWarningUppercase('rfc', $event)" />
            </div>

            <div class="settings-field billing-span-2-half">
              <label>Nombre o razón social *</label>
              <input type="text" class="form-control" v-model.trim="billingWarningForm.legal_name" @input="onBillingWarningUppercase('legal_name', $event)" />
            </div>

            <div class="settings-field billing-span-1">
              <label>Código postal *</label>
              <input type="text" class="form-control" v-model.trim="billingWarningForm.zipcode" maxlength="5" inputmode="numeric" @input="onBillingWarningZipcodeInput" />
            </div>

            <div class="settings-field billing-span-2">
              <label>Régimen fiscal *</label>
              <select class="form-control" v-model="billingWarningForm.fiscal_regimen">
                <option value="">Selecciona un régimen fiscal</option>
                <option v-for="regimen in billingRegimenCatalog" :key="regimen.code" :value="regimen.code">
                  {{ regimen.code }} - {{ regimen.name }}
                </option>
              </select>
            </div>

            <div class="settings-field billing-span-2">
              <label>Uso de CFDI *</label>
              <select class="form-control" v-model="billingWarningForm.cfdi_use">
                <option value="">Selecciona un uso de CFDI</option>
                <option v-for="cfdiUse in billingCfdiUseCatalog" :key="cfdiUse.code" :value="cfdiUse.code">
                  {{ cfdiUse.code }} - {{ cfdiUse.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="payment-modal-footer">
          <button type="button" class="btn btn-light" :disabled="purchasingSubscription || savingBillingWarning" @click="closeBillingWarningModal">
            Cancelar
          </button>
          <button type="button" class="btn btn-outline-primary" :disabled="purchasingSubscription || savingBillingWarning" @click="saveBillingFromWarningModalAndContinue">
            {{ savingBillingWarning ? 'Guardando...' : 'Guardar y continuar con facturación' }}
          </button>
          <button v-if="!hasStartedBillingCapture" type="button" class="btn btn-primary" :disabled="purchasingSubscription || savingBillingWarning" @click="continuePurchaseWithPublicGeneral">
            Continuar con público en general
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import accountService from '@/services/account';

export default {
  name: 'SettingsSubscriptionSection',
  data() {
    return {
      loading: false,
      subscription: null,
      showCardModal: false,
      showDeleteCardModal: false,
      showPurchaseModal: false,
      showPurchaseResultModal: false,
      showBillingWarningModal: false,
      savingCard: false,
      deletingCard: false,
      quotingPurchase: false,
      purchasingSubscription: false,
      savingBillingWarning: false,
      parsingBillingWarningCif: false,
      loadingBillingProfile: false,
      purchaseQuote: null,
      purchaseResult: null,
      billingProfile: null,
      billingRegimenCatalog: [],
      billingCfdiUseCatalog: [],
      billingWarningFormError: '',
      billingWarningCifError: '',
      billingWarningCifFile: null,
      billingWarningForm: this.getEmptyBillingForm(),
      reloadOnPurchaseResultClose: false,
      sectionError: '',
      purchaseFormError: '',
      purchaseModalError: '',
      cardFormError: '',
      deleteCardError: '',
      issuingInvoiceChargeId: null,
      cardFieldErrors: this.getEmptyCardFieldErrors(),
      cardModalForm: this.getEmptyCardForm(),
      selectedPackageId: null,
    };
  },
  computed: {
    packageCatalog() {
      return Array.isArray(this.subscription?.packages_catalog) ? this.subscription.packages_catalog : [];
    },
    currentPackage() {
      return this.subscription?.current_package || null;
    },
    packageName() {
      return this.currentPackage?.name || 'Paquete no asignado';
    },
    packageRfcText() {
      const limit = this.currentPackage?.rfc_limit;

      if (limit === null || limit === undefined) {
        return 'RFCs ilimitados';
      }

      return `${limit} RFC${Number(limit) === 1 ? '' : 's'} incluidos`;
    },
    validityRange() {
      const periods = Array.isArray(this.subscription?.periods) ? this.subscription.periods : [];
      const active = periods.find((item) => item.status === 'active') || periods[0];

      if (!active) {
        return 'Sin periodo vigente';
      }

      return `${this.formatDate(active.starts_at)} - ${this.formatDate(active.ends_at)}`;
    },
    subscriptionStatusText() {
      const status = this.subscription?.status;
      if (!status) return 'Sin informacion de vigencia';
      if (status.has_active_period) {
        const days = Number(status.days_remaining || 0);
        return `Vigente (${days} dia(s) restantes)`;
      }
      return 'Vencida';
    },
    nextPaymentDate() {
      const activeUntil = this.subscription?.status?.active_until;
      if (!activeUntil) return 'Sin fecha programada';
      return this.formatDate(activeUntil);
    },
    nextPaymentAmount() {
      const value = this.packagePrice(this.currentPackage);
      const cycle = this.packageCycleLabel(this.currentPackage);
      return `${this.formatCurrency(value)} / ${cycle}`;
    },
    paymentMethods() {
      return Array.isArray(this.subscription?.payment_methods) ? this.subscription.payment_methods : [];
    },
    primaryCard() {
      return this.paymentMethods.find((item) => !!item.is_default) || this.paymentMethods[0] || null;
    },
    selectedPackage() {
      return this.packageCatalog.find((item) => Number(item.id) === Number(this.selectedPackageId)) || null;
    },
    paymentHistory() {
      const charges = Array.isArray(this.subscription?.charges) ? this.subscription.charges : [];
      if (charges.length > 0) {
        return charges.slice(0, 20).map((item, index) => ({
          id: `charge-${item.id || index}`,
          chargeId: item.id,
          date: item.charged_at,
          description: item.description || 'Cargo de suscripción',
          amount: Number(item.amount || 0),
          status: 'paid',
          invoiceStatus: String(item.invoice_status || ''),
          invoiceUuid: item.invoice_uuid || null,
          invoiceXmlUrl: item.invoice_xml_url || null,
          invoiceError: item.invoice_error || null,
        }));
      }

      const periods = Array.isArray(this.subscription?.periods) ? this.subscription.periods : [];

      const billablePeriods = periods.filter((item) => {
        const origin = String(item?.origin || '').toLowerCase();
        return !origin.includes('trial');
      });

      return billablePeriods.slice(0, 8).map((item, index) => ({
        id: `pay-${item.id || index}`,
        date: item.created_at || item.starts_at,
        description: this.periodDescription(item),
        amount: this.packagePrice(this.currentPackage),
        status: item.status === 'active' || item.status === 'expired' ? 'paid' : 'pending',
        invoiceStatus: '',
        invoiceUuid: null,
        invoiceXmlUrl: null,
      }));
    },
    hasStartedBillingCapture() {
      if (this.billingWarningCifFile) {
        return true;
      }

      const form = this.billingWarningForm || {};
      return ['rfc', 'legal_name', 'fiscal_regimen', 'zipcode', 'cfdi_use']
        .some((field) => String(form[field] || '').trim() !== '');
    },
  },
  async mounted() {
    await this.loadSubscription();
  },
  methods: {
    async loadSubscription() {
      this.loading = true;
      this.sectionError = '';

      try {
        const response = await accountService.getSubscription();

        if (response && response.type === 'success') {
          this.subscription = response.data || null;
          this.selectedPackageId = this.subscription?.current_package?.id || this.packageCatalog[0]?.id || null;
          return;
        }

        this.subscription = null;
      } catch (error) {
        this.subscription = null;
        this.sectionError = 'No se pudo cargar la información de suscripción.';
      } finally {
        this.loading = false;
      }
    },
    periodDescription(item) {
      const packageName = this.currentPackage?.name || 'Suscripción';
      const cycleWord = this.packageCycleLabel(this.currentPackage);
      if (item.origin === 'trial_new') return `Trial inicial - ${packageName}`;
      if (item.origin === 'trial_legacy') return `Trial promocional - ${packageName}`;
      if (item.origin === 'renewal') return `Renovación ${cycleWord} - ${packageName}`;
      if (item.origin === 'purchase') return `Alta de suscripción - ${packageName}`;
      return `${packageName} (${item.origin || 'periodo'})`;
    },
    packageBillingCycle(item) {
      const configured = String(item?.billing_cycle || '').trim().toLowerCase();
      if (configured === 'monthly' || configured === 'annual') {
        return configured;
      }

      const planId = String(item?.conekta_plan_id || '').toLowerCase();
      const code = String(item?.code || '').toLowerCase();

      if (planId.includes('month') || planId.includes('mensual') || code.includes('month') || code.includes('mensual')) {
        return 'monthly';
      }

      return 'annual';
    },
    packageCycleLabel(item) {
      return this.packageBillingCycle(item) === 'monthly' ? 'mensual' : 'anual';
    },
    packagePrice(item) {
      return Number(item?.price || 0);
    },
    packageOptionLabel(item) {
      const limit = item?.rfc_limit;
      const limitText = limit === null || limit === undefined
        ? 'Ilimitado'
        : `${limit} RFC${Number(limit) === 1 ? '' : 's'}`;
      const cycleLabel = this.packageCycleLabel(item);

      return `${item?.name || 'Paquete'} - ${limitText} - ${this.formatCurrency(this.packagePrice(item))} / ${cycleLabel}`;
    },
    getEmptyCardForm() {
      return {
        holder: '',
        number: '',
        expiration: '',
        expMonth: '',
        expYear: '',
        cvv: '',
        street: '',
        colony: '',
        city: '',
        state: '',
        zipcode: '',
        country: 'MEXICO',
      };
    },
    getEmptyCardFieldErrors() {
      return {
        holder: '',
        number: '',
        expiration: '',
        cvv: '',
        street: '',
        colony: '',
        city: '',
        state: '',
        zipcode: '',
      };
    },
    getEmptyBillingForm() {
      return {
        rfc: '',
        legal_name: '',
        fiscal_regimen: '',
        zipcode: '',
        cfdi_use: '',
      };
    },
    fillBillingWarningFormFromProfile() {
      const profile = this.billingProfile || {};
      this.billingWarningForm = {
        rfc: String(profile.rfc || '').trim(),
        legal_name: String(profile.legal_name || '').trim(),
        fiscal_regimen: String(profile.fiscal_regimen || '').trim(),
        zipcode: String(profile.zipcode || '').trim(),
        cfdi_use: String(profile.cfdi_use || '').trim(),
      };
    },
    validateCardField(field) {
      const value = String(this.cardModalForm[field] || '').trim();

      if (field === 'holder') {
        return value ? '' : 'El titular es requerido.';
      }

      if (field === 'number') {
        return /^[0-9]{15,16}$/.test(value) ? '' : 'Debe contener 15 o 16 dígitos.';
      }

      if (field === 'expiration') {
        return this.parseExpiration(value) ? '' : 'Formato inválido. Usa MM/AA.';
      }

      if (field === 'cvv') {
        return /^[0-9]{3,4}$/.test(value) ? '' : 'Debe contener 3 o 4 dígitos.';
      }

      if (field === 'street') {
        return value ? '' : 'La dirección es requerida.';
      }

      if (field === 'colony') {
        return value ? '' : 'La colonia es requerida.';
      }

      if (field === 'city') {
        return value ? '' : 'La ciudad es requerida.';
      }

      if (field === 'state') {
        return value ? '' : 'El estado es requerido.';
      }

      if (field === 'zipcode') {
        return /^[0-9]{5}$/.test(value) ? '' : 'Debe contener 5 dígitos.';
      }

      return '';
    },
    touchCardField(field) {
      this.cardFieldErrors[field] = this.validateCardField(field);
    },
    touchAllCardFields() {
      Object.keys(this.cardFieldErrors).forEach((field) => {
        this.cardFieldErrors[field] = this.validateCardField(field);
      });
    },
    openCardModal() {
      if (this.primaryCard) {
        return;
      }

      this.cardFormError = '';
      this.cardModalForm = this.getEmptyCardForm();
      this.cardFieldErrors = this.getEmptyCardFieldErrors();

      this.showCardModal = true;
    },
    closeCardModal() {
      this.showCardModal = false;
      this.savingCard = false;
      this.cardFormError = '';
      this.cardFieldErrors = this.getEmptyCardFieldErrors();
      this.cardModalForm = this.getEmptyCardForm();
    },
    openDeleteCardModal() {
      if (!this.primaryCard || this.deletingCard) {
        return;
      }

      this.deleteCardError = '';
      this.showDeleteCardModal = true;
    },
    closeDeleteCardModal() {
      if (this.deletingCard) {
        return;
      }

      this.showDeleteCardModal = false;
      this.deleteCardError = '';
    },
    async openPurchaseModal() {
      this.purchaseFormError = '';
      this.purchaseModalError = '';

      if (!this.primaryCard) {
        this.purchaseFormError = 'Debes registrar una tarjeta antes de comprar un paquete.';
        return;
      }

      if (!this.selectedPackageId) {
        this.purchaseFormError = 'Selecciona un paquete para continuar.';
        return;
      }

      this.quotingPurchase = true;
      try {
        const response = await accountService.quoteSubscriptionPurchase({
          package_id: this.selectedPackageId,
        });

        if (response?.type !== 'success') {
          this.purchaseFormError = response?.message || 'No se pudo calcular la compra del paquete.';
          return;
        }

        this.purchaseQuote = response.data || null;
        this.showPurchaseModal = true;
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        this.purchaseFormError = apiMessage || 'No se pudo calcular la compra del paquete.';
      } finally {
        this.quotingPurchase = false;
      }
    },
    closePurchaseModal(force = false) {
      if (this.purchasingSubscription && !force) {
        return;
      }

      this.showPurchaseModal = false;
      this.purchaseQuote = null;
      this.purchaseModalError = '';
    },
    closePurchaseResultModal() {
      const shouldReload = this.reloadOnPurchaseResultClose;
      this.showPurchaseResultModal = false;
      this.purchaseResult = null;
      this.reloadOnPurchaseResultClose = false;

      if (shouldReload) {
        window.location.reload();
      }
    },
    async ensureBillingProfileLoaded(force = false) {
      if (!force && this.billingProfile !== null) {
        return this.billingProfile;
      }

      if (this.loadingBillingProfile) {
        return this.billingProfile;
      }

      this.loadingBillingProfile = true;
      try {
        const response = await accountService.getBilling();
        if (response?.type === 'success') {
          this.billingProfile = response?.data?.billing_profile || null;
          this.billingRegimenCatalog = Array.isArray(response?.data?.catalogs?.regimens) ? response.data.catalogs.regimens : [];
          this.billingCfdiUseCatalog = Array.isArray(response?.data?.catalogs?.cfdi_uses) ? response.data.catalogs.cfdi_uses : [];
        } else {
          this.billingProfile = null;
          this.billingRegimenCatalog = [];
          this.billingCfdiUseCatalog = [];
        }
      } catch (error) {
        this.billingProfile = null;
        this.billingRegimenCatalog = [];
        this.billingCfdiUseCatalog = [];
      } finally {
        this.loadingBillingProfile = false;
      }

      return this.billingProfile;
    },
    hasBillingProfile() {
      if (!this.billingProfile) {
        return false;
      }

      const rfc = String(this.billingProfile.rfc || '').trim();
      const legalName = String(this.billingProfile.legal_name || '').trim();
      const fiscalRegimen = String(this.billingProfile.fiscal_regimen || '').trim();
      const zipcode = String(this.billingProfile.zipcode || '').trim();
      const cfdiUse = String(this.billingProfile.cfdi_use || '').trim();

      return rfc !== '' && legalName !== '' && fiscalRegimen !== '' && zipcode !== '' && cfdiUse !== '';
    },
    openBillingWarningModal() {
      this.billingWarningFormError = '';
      this.billingWarningCifError = '';
      this.billingWarningCifFile = null;
      this.fillBillingWarningFormFromProfile();
      this.showBillingWarningModal = true;
    },
    closeBillingWarningModal() {
      if (this.purchasingSubscription) {
        return;
      }

      this.showBillingWarningModal = false;
    },
    onBillingWarningUppercase(field, event) {
      const value = event?.target?.value || '';
      this.billingWarningForm[field] = value.toUpperCase();
    },
    onBillingWarningZipcodeInput(event) {
      const value = event?.target?.value || '';
      this.billingWarningForm.zipcode = value.replace(/\D/g, '').slice(0, 5);
    },
    async onBillingWarningCifSelected(event) {
      const file = event?.target?.files?.[0] || null;
      this.billingWarningCifFile = file;
      this.billingWarningCifError = '';

      if (!file) {
        return;
      }

      await this.parseBillingWarningCifFile();
    },
    async parseBillingWarningCifFile() {
      if (!this.billingWarningCifFile) {
        return;
      }

      this.billingWarningCifError = '';
      const formData = new FormData();
      formData.append('cif_file', this.billingWarningCifFile);

      this.parsingBillingWarningCif = true;
      try {
        const response = await accountService.parseBillingCif(formData);
        if (response?.type !== 'success') {
          this.billingWarningCifError = response?.message || 'No se pudo leer la cédula fiscal.';
          return;
        }

        const parsed = response?.data?.parsed_data || {};
        this.billingWarningForm = {
          ...this.billingWarningForm,
          rfc: parsed.rfc || this.billingWarningForm.rfc,
          legal_name: parsed.legal_name || this.billingWarningForm.legal_name,
          fiscal_regimen: parsed.fiscal_regimen && this.billingRegimenCatalog.some((item) => item.code === parsed.fiscal_regimen)
            ? parsed.fiscal_regimen
            : this.billingWarningForm.fiscal_regimen,
          zipcode: parsed.zipcode || this.billingWarningForm.zipcode,
          cfdi_use: parsed.cfdi_use && this.billingCfdiUseCatalog.some((item) => item.code === parsed.cfdi_use)
            ? parsed.cfdi_use
            : this.billingWarningForm.cfdi_use,
        };

        this.$toast.success('Se leyó la cédula fiscal y se precargaron los datos detectados.');
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        this.billingWarningCifError = apiMessage || 'No se pudo leer la cédula fiscal.';
      } finally {
        this.parsingBillingWarningCif = false;
      }
    },
    validateBillingWarningForm() {
      this.billingWarningFormError = '';
      if (!this.billingWarningForm.rfc || !this.billingWarningForm.legal_name || !this.billingWarningForm.fiscal_regimen || !this.billingWarningForm.zipcode || !this.billingWarningForm.cfdi_use) {
        this.billingWarningFormError = 'RFC, razón social, régimen fiscal, código postal y uso de CFDI son obligatorios.';
        return false;
      }

      return true;
    },
    async saveBillingFromWarningModalAndContinue() {
      if (!this.validateBillingWarningForm()) {
        return;
      }

      this.billingWarningFormError = '';

      const payload = new FormData();
      payload.append('rfc', this.billingWarningForm.rfc);
      payload.append('legal_name', this.billingWarningForm.legal_name);
      payload.append('fiscal_regimen', this.billingWarningForm.fiscal_regimen);
      payload.append('zipcode', this.billingWarningForm.zipcode);
      payload.append('cfdi_use', this.billingWarningForm.cfdi_use);

      this.savingBillingWarning = true;
      try {
        const response = await accountService.saveBilling(payload);
        if (response?.type !== 'success') {
          this.billingWarningFormError = response?.message || 'No se pudo guardar la información de facturación.';
          return;
        }

        this.billingProfile = response?.data?.billing_profile || null;
        this.$toast.success(response?.message || 'Datos de facturación guardados correctamente.');
        this.showBillingWarningModal = false;
        await this.executePurchase();
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        this.billingWarningFormError = apiMessage || 'No se pudo guardar la información de facturación.';
      } finally {
        this.savingBillingWarning = false;
      }
    },
    async continuePurchaseWithPublicGeneral() {
      this.showBillingWarningModal = false;
      await this.executePurchase();
    },
    async confirmPurchase() {
      if (!this.selectedPackageId) {
        this.purchaseModalError = 'Selecciona un paquete para continuar.';
        return;
      }

      await this.ensureBillingProfileLoaded();
      if (!this.hasBillingProfile()) {
        this.openBillingWarningModal();
        return;
      }

      await this.executePurchase();
    },
    async executePurchase() {
      if (!this.selectedPackageId) {
        this.purchaseModalError = 'Selecciona un paquete para continuar.';
        return;
      }

      const statusBeforePurchase = this.subscription?.status || {};
      const daysRemainingBeforePurchase = Number(statusBeforePurchase.days_remaining ?? 9999);
      const wasExpiredOrExpiringSoonBeforePurchase = Boolean(statusBeforePurchase.is_expired)
        || (Boolean(statusBeforePurchase.has_active_period) && daysRemainingBeforePurchase <= 15);

      this.purchasingSubscription = true;
      try {
        const response = await accountService.purchaseSubscription({
          package_id: this.selectedPackageId,
        });

        if (response?.type !== 'success') {
          this.purchaseModalError = response?.message || 'No fue posible completar la compra.';
          return;
        }

        this.$toast.success(response?.message || 'Compra realizada correctamente.');
        this.purchaseResult = response?.data || {
          operation_type: this.purchaseQuote?.operation_type,
          package: this.purchaseQuote?.package || this.selectedPackage,
          amount: this.purchaseQuote?.amount,
          amount_breakdown: this.purchaseQuote?.amount_breakdown,
          period: {
            ends_at: this.purchaseQuote?.ends_at,
          },
          next_charge_at: this.purchaseQuote?.next_charge_at || this.purchaseQuote?.ends_at,
          ends_at: this.purchaseQuote?.ends_at,
        };
        this.reloadOnPurchaseResultClose = wasExpiredOrExpiringSoonBeforePurchase;
        this.closePurchaseModal(true);
        await this.loadSubscription();
        this.showPurchaseResultModal = true;
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        this.purchaseModalError = apiMessage || 'No fue posible completar la compra.';
      } finally {
        this.purchasingSubscription = false;
      }
    },
    async deleteCard() {
      if (!this.primaryCard) {
        return;
      }

      this.deletingCard = true;
      try {
        const response = await accountService.deleteSubscriptionCard();
        if (response?.type !== 'success') {
          this.deleteCardError = response?.message || 'No se pudo eliminar la tarjeta.';
          return;
        }

        this.$toast.success(response?.message || 'Tarjeta eliminada correctamente.');
        await this.loadSubscription();
        this.showDeleteCardModal = false;
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        this.deleteCardError = apiMessage || 'No se pudo eliminar la tarjeta.';
      } finally {
        this.deletingCard = false;
      }
    },
    onExpirationInput(event) {
      const rawValue = event?.target?.value || '';
      const onlyDigits = rawValue.replace(/\D/g, '').slice(0, 4);

      if (onlyDigits.length <= 2) {
        this.cardModalForm.expiration = onlyDigits;
        return;
      }

      this.cardModalForm.expiration = `${onlyDigits.slice(0, 2)}/${onlyDigits.slice(2)}`;
    },
    onUppercaseInput(field, event) {
      const value = event?.target?.value || '';
      this.cardModalForm[field] = value.toUpperCase();
      if (this.cardFieldErrors[field]) {
        this.touchCardField(field);
      }
    },
    onCardNumberInput(event) {
      const value = event?.target?.value || '';
      this.cardModalForm.number = value.replace(/\D/g, '').slice(0, 16);
      if (this.cardFieldErrors.number) {
        this.touchCardField('number');
      }
    },
    onCvvInput(event) {
      const value = event?.target?.value || '';
      this.cardModalForm.cvv = value.replace(/\D/g, '').slice(0, 4);
      if (this.cardFieldErrors.cvv) {
        this.touchCardField('cvv');
      }
    },
    onZipcodeInput(event) {
      const value = event?.target?.value || '';
      this.cardModalForm.zipcode = value.replace(/\D/g, '').slice(0, 5);
      if (this.cardFieldErrors.zipcode) {
        this.touchCardField('zipcode');
      }
    },
    parseExpiration(value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 4);
      if (digits.length !== 4) {
        return null;
      }

      const month = digits.slice(0, 2);
      const year = digits.slice(2, 4);
      const monthNumber = Number(month);

      if (monthNumber < 1 || monthNumber > 12) {
        return null;
      }

      return { month, year };
    },
    validateCardModalForm() {
      this.cardFormError = '';
      this.touchAllCardFields();
      const hasErrors = Object.values(this.cardFieldErrors).some((message) => !!message);
      if (hasErrors) {
        this.cardFormError = 'Corrige los campos marcados para continuar.';
        return false;
      }

      const parsed = this.parseExpiration(this.cardModalForm.expiration);

      this.cardModalForm.expMonth = parsed.month;
      this.cardModalForm.expYear = parsed.year;
      return true;
    },
    async ensureConektaLoaded() {
      if (window.Conekta && typeof window.Conekta.Token?.create === 'function') {
        return;
      }

      const existing = document.querySelector('script[data-conekta-sdk="true"]');
      if (existing) {
        if (window.Conekta && typeof window.Conekta.Token?.create === 'function') {
          return;
        }

        await new Promise((resolve, reject) => {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', reject, { once: true });
        });
        return;
      }

      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.conekta.io/js/latest/conekta.js';
        script.async = true;
        script.defer = true;
        script.dataset.conektaSdk = 'true';
        script.onload = resolve;
        script.onerror = () => reject(new Error('No se pudo cargar Conekta.js'));
        document.head.appendChild(script);
      });
    },
    async createConektaToken(formElement) {
      return new Promise((resolve, reject) => {
        window.Conekta.Token.create(
          formElement,
          (token) => resolve(token),
          (error) => reject(error),
        );
      });
    },
    async submitCardModal() {
      this.cardFormError = '';
      if (!this.validateCardModalForm()) {
        return;
      }

      this.savingCard = true;

      try {
        const conektaPublicKey = String(process.env.VUE_APP_CONEKTA_PUBLIC_KEY || '').trim();
        if (!conektaPublicKey) {
          this.cardFormError = 'No se ha configurado la llave pública de Conekta (VUE_APP_CONEKTA_PUBLIC_KEY).';
          return;
        }

        await this.ensureConektaLoaded();

        if (!window.Conekta || typeof window.Conekta.Token?.create !== 'function') {
          this.cardFormError = 'No fue posible inicializar Conekta en el navegador.';
          return;
        }

        window.Conekta.setPublicKey(conektaPublicKey);

        const token = await this.createConektaToken(this.$refs.cardForm);
        const payload = {
          name: this.cardModalForm.holder,
          card_last4: this.cardModalForm.number.slice(-4),
          exp_month: this.cardModalForm.expMonth,
          exp_year: this.cardModalForm.expYear,
          street: this.cardModalForm.street,
          colony: this.cardModalForm.colony,
          city: this.cardModalForm.city,
          state: this.cardModalForm.state,
          country: this.cardModalForm.country,
          zipcode: this.cardModalForm.zipcode,
          conekta_token_id: token?.id || '',
          brand: token?.card?.brand || this.primaryCard?.brand || 'TARJETA',
        };

        const response = await accountService.storeSubscriptionCard(payload);

        if (response?.type !== 'success') {
          this.cardFormError = response?.message || 'No se pudo guardar la tarjeta.';
          return;
        }

        this.$toast.success(response?.message || 'Tarjeta guardada correctamente.');
        await this.loadSubscription();
        this.closeCardModal();
      } catch (error) {
        const purchaserMessage = error?.message_to_purchaser;
        const apiMessage = error?.response?.data?.message;
        this.cardFormError = purchaserMessage || apiMessage || 'No se pudo tokenizar la tarjeta. Verifica los datos e intenta nuevamente.';
      } finally {
        this.savingCard = false;
      }
    },
    cardHolder(card) {
      return card?.name || card?.holder_name || card?.cardholder_name || 'Sin titular';
    },
    cardAddress(card) {
      const parts = [
        card?.street || card?.address_street || card?.address_line1,
        card?.colony || card?.address_colony || card?.address_line2,
        card?.city || card?.address_city,
        card?.state || card?.address_state,
        card?.country || card?.address_country,
      ].filter((item) => !!item);

      const zip = card?.zipcode || card?.postal_code || card?.address_zip;
      if (zip) {
        parts.push(`C.P. ${zip}`);
      }

      return parts.length ? parts.join(', ') : 'Sin dirección';
    },
    cardExpiration(card) {
      const month = String(card?.exp_month || '').padStart(2, '0');
      const year = String(card?.exp_year || '');
      return month && year ? `${month}/${year}` : '--/--';
    },
    cardExpirationForInput(card) {
      const month = String(card?.exp_month || '').padStart(2, '0');
      const year = String(card?.exp_year || '').slice(-2);
      return month && year ? `${month}/${year}` : '';
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(amount);
    },
    canDownloadInvoiceXml(item) {
      return Boolean(item?.chargeId && (item?.invoiceXmlUrl || item?.invoiceUuid));
    },
    isInvoiceIssued(item) {
      const status = String(item?.invoiceStatus || '').toLowerCase();
      return status === 'success' || status === 'issued' || this.canDownloadInvoiceXml(item) || this.canDownloadInvoicePdf(item);
    },
    shouldShowInvoiceDownloads(item) {
      return this.isInvoiceIssued(item);
    },
    canIssueInvoice(item) {
      if (!item?.chargeId) {
        return false;
      }

      const status = String(item?.invoiceStatus || '').toLowerCase();
      if (status === 'skipped') {
        return false;
      }

      return !this.isInvoiceIssued(item);
    },
    canDownloadInvoicePdf(item) {
      if (!item?.chargeId) {
        return false;
      }

      const status = String(item?.invoiceStatus || '').toLowerCase();
      return status === 'success' || status === 'issued' || Boolean(item?.invoiceUuid);
    },
    triggerBlobDownload(blob, fileName) {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    },
    extractApiErrorMessage(error, fallbackMessage) {
      const directMessage = error?.response?.data?.message;
      if (directMessage) {
        return directMessage;
      }

      const nestedMessage = error?.message;
      if (nestedMessage && !nestedMessage.toLowerCase().includes('network error')) {
        return nestedMessage;
      }

      return fallbackMessage;
    },
    async downloadInvoiceXml(item) {
      if (!this.canDownloadInvoiceXml(item)) {
        this.$toast.warning('Este cargo aún no tiene XML disponible.');
        return;
      }

      try {
        const response = await accountService.downloadSubscriptionInvoiceXml(item.chargeId);
        const blob = new Blob([response], { type: 'application/xml' });
        const suffix = item?.invoiceUuid || `charge-${item.chargeId}`;
        this.triggerBlobDownload(blob, `${suffix}.xml`);
      } catch (error) {
        const message = this.extractApiErrorMessage(error, 'No fue posible descargar el XML.');
        this.$toast.error(message);
      }
    },
    async downloadInvoicePdf(item) {
      if (!this.canDownloadInvoicePdf(item)) {
        this.$toast.warning('Este cargo aún no tiene PDF disponible.');
        return;
      }

      try {
        const response = await accountService.downloadSubscriptionInvoicePdf(item.chargeId);
        const blob = new Blob([response], { type: 'application/pdf' });
        const suffix = item?.invoiceUuid || `charge-${item.chargeId}`;
        this.triggerBlobDownload(blob, `${suffix}.pdf`);
      } catch (error) {
        const message = this.extractApiErrorMessage(error, 'No fue posible descargar el PDF.');
        this.$toast.error(message);
      }
    },
    async issueInvoice(item) {
      if (!this.canIssueInvoice(item)) {
        return;
      }

      this.issuingInvoiceChargeId = item.chargeId;
      try {
        const response = await accountService.issueSubscriptionInvoice(item.chargeId);
        if (response?.type !== 'success') {
          this.$toast.error(response?.message || 'No fue posible facturar este pago.');
          return;
        }

        this.$toast.success(response?.message || 'La factura se emitió correctamente.');
        await this.loadSubscription();
      } catch (error) {
        const message = this.extractApiErrorMessage(error, 'No fue posible facturar este pago.');
        this.$toast.error(message);
      } finally {
        this.issuingInvoiceChargeId = null;
      }
    },
  },
};
</script>
